/**
 * useWorkflowValidation.js
 *
 * Custom hook that validates each workflow step.
 * Checks preconditions against workspace state and verifies required params.
 */

import { useState, useEffect } from 'react';
import { getAction, checkPreconditions } from '../../../gibbs/registry';
import { StepStatus, VALIDATION_DEBOUNCE_MS } from './constants';
import { displayStepParams } from './stepParamOverrides';

function hasInvalidOptionValue(actionDef, params) {
    return (actionDef.parameters || []).some((p) => {
        if (!Array.isArray(p.options) || p.options.length === 0) return false;
        if (!Object.prototype.hasOwnProperty.call(params, p.name)) return false;
        const val = params[p.name];
        if (val === undefined || val === null || val === '') return false;
        return !p.options.includes(val);
    });
}

export function validateWorkflowSteps(plan, stepParams, workspaceState) {
    const actions = plan?.actions;
    if (!actions || actions.length === 0) {
        return { stepValidation: {}, isAllValid: false };
    }

    const validation = {};
    let allValid = true;

    for (let i = 0; i < actions.length; i++) {
        const action = actions[i];
        const actionDef = getAction(action.actionId);

        // Unknown action
        if (!actionDef) {
            validation[i] = StepStatus.INVALID;
            allValid = false;
            continue;
        }

        // Planned (not yet implemented) actions get WARNING
        if (actionDef.status === 'planned') {
            validation[i] = StepStatus.WARNING;
            allValid = false;
            continue;
        }

        // Check preconditions if workspace state is available
        if (workspaceState && actionDef.preconditions?.length > 0) {
            const { met } = checkPreconditions(action.actionId, workspaceState);
            if (!met) {
                validation[i] = StepStatus.INVALID;
                allValid = false;
                continue;
            }
        }

        // Check required parameters are filled. A param the plan already
        // supplies counts as filled: reading the overrides alone reported a
        // fully-specified step as invalid until the user retyped its values.
        const params = displayStepParams(action, stepParams?.[i]);
        const requiredParams = (actionDef.parameters || []).filter((p) => p.required);
        const missingRequired = requiredParams.some((p) => {
            const val = params[p.name];
            return val === undefined || val === null || val === '';
        });

        if (missingRequired || hasInvalidOptionValue(actionDef, params)) {
            validation[i] = StepStatus.INVALID;
            allValid = false;
            continue;
        }

        validation[i] = StepStatus.VALID;
    }

    return { stepValidation: validation, isAllValid: allValid };
}

/**
 * Validate all steps in a workflow plan.
 *
 * @param {Object|null} plan - ActionPlan with .actions array
 * @param {Object} stepParams - { [stepIndex]: { ...params } }
 * @param {Object} [workspaceState] - Workspace state for precondition checks (e.g. App.Workspace)
 * @returns {{ stepValidation: Object<number, string>, isAllValid: boolean }}
 */
export function useWorkflowValidation(plan, stepParams, workspaceState) {
    const [stepValidation, setStepValidation] = useState({});
    const [isAllValid, setIsAllValid] = useState(false);

    useEffect(() => {
        const actions = plan?.actions;
        if (!actions || actions.length === 0) {
            setStepValidation({});
            setIsAllValid(false);
            return undefined;
        }

        const timer = setTimeout(() => {
            const { stepValidation: validation, isAllValid: allValid } = validateWorkflowSteps(
                plan,
                stepParams,
                workspaceState,
            );

            setStepValidation(validation);
            setIsAllValid(allValid);
        }, VALIDATION_DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [plan, stepParams, workspaceState]);

    return { stepValidation, isAllValid };
}
